import { TEST_IDS } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { ExternalLinkIcon, InfoIcon, Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CustomDomainPlatformType, CustomDomainsApi, CustomDomainType } from '@va/shared/api/custom-domains';
import {
  ActiveDomainItem,
  InactiveDomainItem,
  PendingDomainItem,
  useAgencyCustomDashboardDomainProvisioningSteps,
} from '@va/shared/custom-domains';
import {
  useCustomDomainSaveButtonState,
  useRefreshPendingDomain,
} from '@va/shared/custom-domains/lib/shared/reusables';
import { getCustomDomainValidationSchema } from '@va/shared/custom-domains/lib/shared/validation-schema';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { getAgencyUi } from '@va/standalone/shared/actions';
import { selectAgencyId } from '@va/standalone/shared/selectors';
import { SubSectionHeader } from '@va/ui/components/white-label-customisation';
import { Button, Chip, fontWeights, InputMessage, Paragraph, TextInput } from '@va/ui/design-system';
import { getWellFormedUrl } from '@va/util/helpers';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useAgencyCustomDashboardDomains } from '../../shared/useAgencyCustomDomains';
import { CustomDashboardDomainCreationModal } from './CustomDashboardDomainCreationModal';
import { useCustomDashboardDomainActions } from './useCustomDashboardDomainActions';

export const CustomDashboardDomainSection = () => {
  const [isLoadingCustomDomainCreation, setIsLoadingCustomDomainCreation] = useState(false);
  const translate = useTranslate();
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyId);

  const { showErrorNotification } = useAddNotification();
  const { confirm } = useConfirmationDialogContext();

  const {
    activeDomain,
    pendingDomain,
    inactiveDomains,
    isLoading: isLoadingCustomDomainsList,
    mutate: mutateCustomDomainsList,
  } = useAgencyCustomDashboardDomains();

  const { handleChange, values, errors, setFieldValue, handleBlur, touched, dirty, resetForm, submitForm } = useFormik({
    initialValues: {
      customDomain: '',
    },
    onSubmit: async ({ customDomain }) => {
      if (!agencyId) return;
      try {
        setIsLoadingCustomDomainCreation(true);
        const res = await CustomDomainsApi.createCompanyDomain({
          companyId: agencyId,
          domain: customDomain,
          platform: CustomDomainPlatformType.AWL,
          type: CustomDomainType.dashboard,
          skipActivation: true,
        });
        await mutateCustomDomainsList();
        resetForm();

        confirm(
          () => {},
          ({ onClose }) => (
            <CustomDashboardDomainCreationModal
              onSuccess={() => {
                resetForm();
              }}
              domain={customDomain}
              isOpen={true}
              closeModal={onClose}
              customDomainId={res.payload.id}
            />
          ),
        );
      } catch (error) {
        console.error(error);
        showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
      } finally {
        setIsLoadingCustomDomainCreation(false);
      }
    },
    enableReinitialize: true,
    validationSchema: getCustomDomainValidationSchema(translate),
  });

  const { activeCustomDomainActions, inactiveCustomDomainsActions, pendingCustomDomainActions } =
    useCustomDashboardDomainActions();

  const { isSaveButtonDisabled, saveButtonTooltip } = useCustomDomainSaveButtonState({
    pendingDomain,
    dirty,
    isLoading: isLoadingCustomDomainsList,
  });

  useEffect(() => {
    if (!activeDomain?.id) return;
    dispatch(getAgencyUi());
  }, [activeDomain?.id, dispatch]);

  useRefreshPendingDomain({ pendingDomain: pendingDomain, mutate: mutateCustomDomainsList });

  const { steps } = useAgencyCustomDashboardDomainProvisioningSteps();

  return (
    <div>
      <SubSectionHeader
        text={translate('standalone.customPlatformDomain.sectionTitle')}
        tooltip={translate('standalone.customPlatformDomain.sectionTooltip')}
      />
      <div className='space-y-3'>
        {activeDomain && (
          <>
            <ActiveDomainItem
              label={translate('labels.customDomain')}
              value={activeDomain.domain}
              actions={activeCustomDomainActions(activeDomain)}
            />
            <Chip
              className={'rounded-15! p-1.5! pr-3! md:overflow-hidden'}
              icon={<InfoIcon className='w-18px h-18px shrink-0' color='#FCC573' />}
              tooltip={translate('standalone.customDomains.platform.useUpdatedSnippetNotificationTooltip')}
            >
              {translate('standalone.customDomains.platform.useUpdatedSnippetNotification')}
            </Chip>
          </>
        )}

        {pendingDomain && (
          <PendingDomainItem
            label={translate('labels.customDomain')}
            value={pendingDomain.domain}
            step={pendingDomain.step}
            status={pendingDomain.status}
            actions={pendingCustomDomainActions(pendingDomain)}
            steps={steps}
          />
        )}

        {inactiveDomains &&
          inactiveDomains.map((item) => (
            <InactiveDomainItem
              key={item.id}
              label={translate('labels.customDomain')}
              value={item.domain}
              status={item.status}
              step={item.step}
              actions={inactiveCustomDomainsActions(item)}
              steps={steps}
            />
          ))}

        <div className='w-full flex gap-3 items-center bg-white-snow rounded-18 text-gray-devil py-6px px-3 overflow-x-auto scrollbar scrollbar-thumb'>
          {isLoadingCustomDomainsList ? (
            <Skeleton className='h-40 rounded-18' />
          ) : (
            <>
              <div>
                <Paragraph colorClassName='text-gray-devil' weight={fontWeights.medium}>
                  https://
                </Paragraph>
              </div>
              <TextInput
                wrapperClassName='overflow-hidden grow'
                placeholder={translate('standalone.awl.customDomain.input.placeholder')}
                label={translate('standalone.awl.customDomain.input.label')}
                name='customDomain'
                value={values.customDomain}
                clearField={() => setFieldValue('customDomain', '')}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                error={(touched.customDomain && errors.customDomain) || undefined}
                showErrorMessage={false}
                bgColor='#F0F0F0'
                bgColorHover='#F9F9F9'
              />

              <Button
                onClick={() => {
                  window.open(getWellFormedUrl(values.customDomain));
                }}
                disabled={!values.customDomain}
                color='quaternary'
                icon={() => <ExternalLinkIcon color='#3C3C3C' />}
                data-testid={TEST_IDS.generic.buttons.goToWebsite}
              />
            </>
          )}
        </div>

        {touched.customDomain && errors.customDomain && <InputMessage error={errors.customDomain} />}

        <Button
          disabled={isSaveButtonDisabled}
          tooltip={saveButtonTooltip}
          tooltipProps={{ maxWidth: 'max-w-100' }}
          text={translate('button.saveChanges')}
          className='w-full justify-center'
          icon={isLoadingCustomDomainCreation ? () => <Spinner /> : undefined}
          onClick={() => {
            submitForm();
          }}
        />
      </div>
    </div>
  );
};
